import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { FormTemplate } from "@/api/generated/client/models";
import { useToast } from "@/hooks/use-toast";

interface SendFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: FormTemplate[];
  onSubmit: (data: { formTemplateId: number; recipientName: string; recipientEmail: string }) => void;
  isSending: boolean;
}

export function SendFormModal({ isOpen, onClose, templates, onSubmit, isSending }: SendFormModalProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    formTemplateId: '',
    recipientName: '',
    recipientEmail: '',
  });

  const handleClose = () => {
    setFormData({
      formTemplateId: '',
      recipientName: '',
      recipientEmail: '',
    });
    onClose();
  };

  const handleSubmit = () => {
    if (!formData.formTemplateId || !formData.recipientName.trim() || !formData.recipientEmail.trim()) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.recipientEmail)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    onSubmit({
      formTemplateId: parseInt(formData.formTemplateId),
      recipientName: formData.recipientName,
      recipientEmail: formData.recipientEmail,
    });

    // Don't close here — let the parent close on mutation success
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Send Form to Homeowner</DialogTitle>
          <DialogDescription>
            Select a form template and enter the homeowner's information.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Template Select */}
          <div className="space-y-2">
            <Label htmlFor="template">Form Template *</Label>
            <Select
              value={formData.formTemplateId}
              onValueChange={(value) => setFormData(prev => ({ ...prev, formTemplateId: value }))}
            >
              <SelectTrigger id="template" className="truncate">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id.toString()} title={template.name}>
                    <span className="truncate block max-w-[300px]">{template.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Recipient Name */}
          <div className="space-y-2">
            <Label htmlFor="recipient-name">Recipient Name *</Label>
            <Input
              id="recipient-name"
              placeholder="John Doe"
              value={formData.recipientName}
              onChange={(e) => setFormData(prev => ({ ...prev, recipientName: e.target.value }))}
            />
          </div>

          {/* Recipient Email */}
          <div className="space-y-2">
            <Label htmlFor="recipient-email">Recipient Email *</Label>
            <Input
              id="recipient-email"
              type="email"
              placeholder="john@example.com"
              value={formData.recipientEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, recipientEmail: e.target.value }))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSending || !formData.formTemplateId || !formData.recipientName.trim() || !formData.recipientEmail.trim()}
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Form'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
